import get from 'lodash/get';
import PropTypes from 'prop-types';
import React from 'react';
import branch from 'recompose/branch';
import compose from 'recompose/compose';
import renderNothing from 'recompose/renderNothing';
import renderComponent from 'recompose/renderComponent';
import setDisplayName from 'recompose/setDisplayName';
import { connect } from 'react-redux';
import { withApollo } from '@apollo/react-hoc';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { connectedRouterRedirect } from 'redux-auth-wrapper/history4/redirect';
import Loader from '../../components/Loader';
import communityProfileQuery from './community-profile-query';
import * as constants from './constants';

export const hasInstitution = (viewer) =>
  viewer.idToken && (
    (viewer.isNaviance && viewer.idToken[constants.SCHOOL_ID]) ||
    (!viewer.isNaviance && viewer.idToken['custom:institution'])
  );

export const isUserAdmin = (viewer) =>
  viewer.idToken && (
    (viewer.isNaviance && viewer.idToken[constants.NAVIANCE_ADMIN_ROLE]) ||
    (!viewer.isNaviance && viewer.idToken['custom:role'] === constants.NON_NAVIANCE_ADMIN_ROLE)
  );

export const isUserMember = (viewer) =>
  viewer.idToken && (
    (viewer.isNaviance && viewer.idToken[constants.NAVIANCE_MEMBER_ROLE]) ||
    (!viewer.isNaviance && viewer.idToken['custom:role'] === constants.NON_NAVIANCE_MEMBER_ROLE)
  );

export const isUserAuthorized = (viewer) =>
  viewer && viewer.isAuthenticated && hasInstitution(viewer) &&
    (isUserAdmin(viewer) || isUserMember(viewer));

export const authorized = (options={}) =>
  compose(
    connect((state) => ({ viewer: state.current.viewer })),
    setDisplayName('authorized'),
    branch(
      ({ viewer }) => !viewer.isAuthenticated,
      renderComponent(options.failure || renderNothing()),
    ),
  );

export const authorizedOrLogin = compose(
  withRouter,
  connectedRouterRedirect({
    wrapperDisplayName: 'authorizedOrLogin',
    authenticatedSelector: (state) => isUserAuthorized(state.current.viewer),
    redirectPath: '/login',
  }),
);

export const notAuthorized = compose(
  withRouter,
  connectedRouterRedirect({
    wrapperDisplayName: 'NotAuthorized',
    authenticatedSelector: (state) => !isUserAuthorized(state.current.viewer),
    redirectPath: (state, props) => queryString.parse(props.location.search).redirect || '/',
    allowRedirectBack: false,
  }),
);

export const authorizedWithAdminRole = (options={}) =>
  compose(
    connect((state) => ({ viewer: state.current.viewer })),
    setDisplayName('authorizedWithRole'),
    branch(
      ({ viewer }) => !viewer.isAuthenticated || !isUserAdmin(viewer),
      renderComponent(options.failure || renderNothing()),
    ),
  );

export const requireCommunityProfile = (Component) => {
  class ProfileAuthorizer extends React.PureComponent {
    static propTypes = {
      client: PropTypes.shape({
        query: PropTypes.func.isRequired,
        readQuery: PropTypes.func.isRequired,
      }).isRequired,
      history: PropTypes.shape({
        push: PropTypes.func.isRequired,
      }).isRequired,
    };

    constructor(props) {
      super(props);
      const { client } = this.props;
      // read field_activated_account from cache without triggering server request
      // no need to make requests if profile is activated
      try {
        const { viewer } = client.readQuery({
          query: communityProfileQuery,
        });
        const hasServerlessCC = viewer.enabledFeatures?.includes('COMMUNITYSERVERLESS');
        if (get(viewer, 'profile.field_activated_account') && (!hasServerlessCC || get(viewer, 'profile.field_account_consent'))) {
          this.state = { render: true };
          return;
        }
      } catch (e) {
        // errors = no active profile
      }
      this.state = { loading: true };
    }

    async componentDidMount() {
      const { client, history } = this.props;
      const { loading } = this.state;
      if (loading) {
        const response = await client.query({
          query: communityProfileQuery,
          fetchPolicy: 'network-only',
        });
        const state = { loading: false };
        const hasServerlessCC = get(response, 'data.viewer.enabledFeatures')?.includes('COMMUNITYSERVERLESS');
        try {
          if (get(response, 'data.viewer.profile.field_activated_account') && (!hasServerlessCC || get(response, 'data.viewer.profile.field_account_consent'))) {
            state.render = true;
            return; // skip history.push()
          }
        } finally {
          // eslint-disable-next-line react/no-did-mount-set-state
          this.setState(state);
        }
        const isCognitoUser = get(this.props, 'viewer.idToken.cognito:username', false);
        if (hasServerlessCC && isCognitoUser) {
          history.push('/counselorcommunity/');
        } else if (hasServerlessCC && !isCognitoUser) {
          history.push('/counselorcommunity/profile/edit');
        } else {
          history.push('/community/');
        }
      }
    }

    render() {
      const { render, loading } = this.state;
      if (loading) {
        return <Loader active />;
      } else if (render) {
        return <Component {...this.props} />;
      }
      return null;
    }
  }

  return compose(
    withApollo,
    withRouter,
  )(ProfileAuthorizer);
};
